import type {
    TranslatableNameWithType,
    TranslationOptions,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import type { Translatable } from "app/pages/sales-intelligence/types";

const isTranslationOptions = (value: unknown): value is TranslationOptions =>
    typeof value === "object" &&
    value !== null &&
    "option" in value &&
    "remainingOptions" in value &&
    typeof (value as TranslationOptions).option === "string" &&
    typeof (value as TranslationOptions).remainingOptions === "number";

export function getSelectionReplacements(
    filterName: Readonly<TranslatableNameWithType>,
): Readonly<TranslationOptions> | undefined {
    const { replacements } = filterName.translatable;

    if (!replacements) {
        return undefined;
    }

    return isTranslationOptions(replacements) ? replacements : undefined;
}

export function isPartialFilterName(filterName: Readonly<TranslatableNameWithType>): boolean {
    const replacements = getSelectionReplacements(filterName);

    return filterName.type === "partial" || (replacements?.remainingOptions ?? 0) > 0;
}

export function filterSelectionTranslatable(filterName: Readonly<TranslatableNameWithType>): Readonly<Translatable> {
    return filterName.translatable;
}

export function getPartialSelectionFromTranslatable(
    translatable: Readonly<Translatable>,
): Readonly<TranslationOptions> | undefined {
    if (!translatable.key.endsWith(".name.partial")) {
        return undefined;
    }

    const { replacements } = translatable;

    if (!replacements) {
        return undefined;
    }

    return isTranslationOptions(replacements) ? replacements : undefined;
}

export function isPartialSelectionTranslatable(translatable: Readonly<Translatable>): boolean {
    return getPartialSelectionFromTranslatable(translatable) !== undefined;
}

/** Singular filter selections use the resolved option label as the translatable key. */
export function isFilterSingularLabel(translatable: Readonly<Translatable>): boolean {
    if (translatable.replacements && Object.keys(translatable.replacements).length > 0) {
        return false;
    }

    return !translatable.key.startsWith("si.");
}
